import React, { Component, createRef } from "react";
import { searchFccFiler, getFccFiler } from "../../config/api_calls";
import HelperClass from "../../config/helperClass";

const auth = new HelperClass();

class FccFilerAutocomplete extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: props.value || "",
      results: [],
      showDropdown: false,
      isSearching: false,
      highlightedIndex: -1,
      noResults: false,
      searchError: "",
      isUsingCache: false,
    };
    this.debounceTimer = null;
    this.lastSearchedQuery = "";
    this.abortController = null;
    this.wrapperRef = createRef();
    
    // Enhanced caching system
    this.cache = new Map(); // { "car": { results: [...], timestamp: 1234567890, baseQuery: "car" } }
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    this.lastApiQuery = ""; // Track last query that triggered API call
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.abortController) this.abortController.abort();
    this.cache.clear();
  }

  componentDidUpdate(prevProps) {
    // Sync external value changes (e.g., when parent sets filer_id from existing data)
    if (
      prevProps.value !== this.props.value &&
      this.props.value !== this.state.query
    ) {
      this.setState({ query: this.props.value || "" });
    }
  }

  handleClickOutside = (event) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(event.target)) {
      this.setState({ showDropdown: false, highlightedIndex: -1 });
    }
  };

  isNumericInput = (str) => /^\d+$/.test(str.trim());

  formatLabel = (item, queryStr) => {
    if (this.isNumericInput(queryStr)) {
      return `${item.filer_id} - ${item.name}`;
    }
    return `${item.name} (${item.filer_id})`;
  };

  // Check if cached data is still valid
  isCacheValid = (cacheKey) => {
    const cached = this.cache.get(cacheKey);
    if (!cached) return false;
    const age = Date.now() - cached.timestamp;
    return age < this.CACHE_DURATION;
  };

  // Get cached results matching the query
  getCachedResults = (query) => {
    const trimmedQuery = query.trim().toLowerCase();
    
    // Try exact match first
    if (this.isCacheValid(trimmedQuery)) {
      return this.cache.get(trimmedQuery).results;
    }
    
    // Find closest cached base query (e.g., "car" for "carri")
    let closestCacheKey = null;
    for (let [key, value] of this.cache.entries()) {
      if (trimmedQuery.startsWith(key) && this.isCacheValid(key)) {
        if (!closestCacheKey || key.length > closestCacheKey.length) {
          closestCacheKey = key;
        }
      }
    }
    
    if (closestCacheKey) {
      const cachedData = this.cache.get(closestCacheKey);
      // Filter cached results locally
      return this.filterResults(cachedData.results, trimmedQuery);
    }
    
    return null;
  };

  // Local filtering of cached results
  filterResults = (results, query) => {
    const lowerQuery = query.toLowerCase();
    return results.filter(item => {
      const nameMatch = item.name.toLowerCase().includes(lowerQuery);
      const filerIdMatch = item.filer_id.includes(lowerQuery);
      return nameMatch || filerIdMatch;
    });
  };

  // Decide if API call is needed
  shouldMakeApiCall = (query, hasCachedData) => {
    const length = query.length;
    const isNumeric = this.isNumericInput(query);
    
    // If no cached data exists at all, make API call
    if (!hasCachedData) return true;
    
    // Always call API at exactly 6 digits (exact Filer ID)
    if (isNumeric && length === 6) return true;
    
    // Specific character lengths for API calls: 3, 8, 15, 20, 30
    const apiCallLengths = [3, 8, 15, 20, 30];
    if (apiCallLengths.includes(length) && query !== this.lastApiQuery) {
      return true;
    }
    
    return false;
  };

  searchFiler = async (query, forceApi = false) => {
    const trimmedQuery = query.trim();
    
    if (trimmedQuery.length < 3) {
      this.setState({
        results: [],
        showDropdown: false,
        noResults: false,
        searchError: "",
        isUsingCache: false,
      });
      this.cache.clear();
      this.lastApiQuery = "";
      return;
    }

    // Try cache first (unless forced API call)
    let cachedResults = null;
    let hasCachedData = false;
    
    if (!forceApi) {
      cachedResults = this.getCachedResults(trimmedQuery);
      hasCachedData = cachedResults !== null;
      
      if (cachedResults !== null) {
        // Show cached results immediately
        this.setState({
          results: cachedResults,
          showDropdown: true,
          isSearching: false,
          noResults: cachedResults.length === 0,
          highlightedIndex: -1,
          isUsingCache: true,
          searchError: "",
        });
        this.lastSearchedQuery = trimmedQuery;
        
        // Auto-select if exactly 6 digits and single result
        if (this.isNumericInput(trimmedQuery) && trimmedQuery.length === 6 && cachedResults.length === 1) {
          setTimeout(() => this.handleSelect(cachedResults[0]), 300);
        }
        
        // Check if we should refresh with API call anyway
        if (!this.shouldMakeApiCall(trimmedQuery, hasCachedData)) {
          return;
        }
        // If shouldMakeApiCall returns true, continue to make API call below
      }
    }

    // Make API call if: forced, no cache, or shouldMakeApiCall says so
    if (!forceApi && hasCachedData && !this.shouldMakeApiCall(trimmedQuery, hasCachedData)) {
      return;
    }

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.setState({ isSearching: true, searchError: "", isUsingCache: false });

    try {
      const response = await searchFccFiler(
        auth.getAccount(),
        auth.getToken(),
        trimmedQuery,
      );

      if (this.abortController && this.abortController.signal.aborted) {
        return;
      }

      this.lastSearchedQuery = trimmedQuery;
      this.lastApiQuery = trimmedQuery;

      if (
        response.data &&
        response.data.status === "success" &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        const results = response.data.data;
        
        // Cache the results
        this.cache.set(trimmedQuery.toLowerCase(), {
          results: results,
          timestamp: Date.now(),
          baseQuery: trimmedQuery.toLowerCase(),
        });

        this.setState({
          results: results,
          showDropdown: true,
          isSearching: false,
          noResults: false,
          highlightedIndex: -1,
        });

        // Auto-select if exactly 6 digits and single result
        if (this.isNumericInput(trimmedQuery) && trimmedQuery.length === 6 && results.length === 1) {
          setTimeout(() => this.handleSelect(results[0]), 300);
        }
      } else {
        this.setState({
          results: [],
          showDropdown: true,
          isSearching: false,
          noResults: true,
          highlightedIndex: -1,
        });
      }
    } catch (error) {
      if (error && error.name === "AbortError") return;
      this.setState({
        isSearching: false,
        searchError: "Error searching. Please try again.",
        showDropdown: true,
        noResults: false,
      });
    }
  };

  handleInputChange = (e) => {
    const value = e.target.value;
    const trimmedValue = value.trim();
    
    // Clear debounce timer
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    
    // Update query state and clear errors
    this.setState({
      query: value,
      noResults: false,
      searchError: "",
    });

    // Notify parent of raw value change
    if (this.props.onInputChange) {
      this.props.onInputChange(value);
    }

    // If field is empty or less than 3 characters, close dropdown and clear
    if (trimmedValue.length < 3) {
      this.setState({
        results: [],
        showDropdown: false,
        noResults: false,
        isUsingCache: false,
      });
      this.lastSearchedQuery = "";
      
      // Only clear cache if field is completely empty
      if (trimmedValue.length === 0) {
        this.cache.clear();
        this.lastApiQuery = "";
      }
      return;
    }

    // Check if we have cached data that can be shown immediately (no debounce)
    const cachedResults = this.getCachedResults(trimmedValue);
    if (cachedResults !== null && cachedResults.length > 0) {
      // Show cached results immediately for instant feedback
      this.setState({
        results: cachedResults,
        showDropdown: true,
        isUsingCache: true,
        noResults: false,
      });
    }

    // Debounce API call at 300ms
    this.debounceTimer = setTimeout(() => {
      this.searchFiler(trimmedValue);
    }, 300);
  };

  handleBlur = () => {
    // Delay to allow click on dropdown items
    setTimeout(() => {
      const trimmedQuery = this.state.query.trim();
      
      // If no option was selected and input >= 3 chars, ensure search completed
      if (
        trimmedQuery.length >= 3 &&
        trimmedQuery !== this.lastSearchedQuery &&
        !this.state.isSearching
      ) {
        // Force API call on blur to ensure we have latest data
        this.searchFiler(trimmedQuery, true);
      }
      // Don't close dropdown immediately — handleClickOutside will handle it
    }, 200);

    // Call parent's onBlur callback
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  handleSelect = async (item) => {
    const filerId = item.filer_id;

    this.setState({
      query: filerId,
      showDropdown: false,
      results: [],
      highlightedIndex: -1,
      noResults: false,
      searchError: "",
      isUsingCache: false,
    });

    this.lastSearchedQuery = "";

    if (this.props.onSelect) {
      this.props.onSelect(filerId);
    }

    if (this.props.onFccDataLoading) {
      this.props.onFccDataLoading(true);
    }

    try {
      const fccResponse = await getFccFiler(
        auth.getAccount(),
        auth.getToken(),
        filerId,
      );

      if (this.props.onFccDataLoaded) {
        this.props.onFccDataLoaded(fccResponse, filerId);
      }
    } catch (error) {
      if (this.props.onFccDataError) {
        this.props.onFccDataError(error);
      }
    }
  };

  handleKeyDown = (e) => {
    const { results, highlightedIndex, showDropdown } = this.state;

    if (!showDropdown || results.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.setState((prev) => ({
          highlightedIndex: Math.min(prev.highlightedIndex + 1, results.length - 1),
        }));
        break;
      case "ArrowUp":
        e.preventDefault();
        this.setState((prev) => ({
          highlightedIndex: Math.max(prev.highlightedIndex - 1, 0),
        }));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          this.handleSelect(results[highlightedIndex]);
        }
        break;
      case "Escape":
        this.setState({ showDropdown: false, highlightedIndex: -1 });
        break;
      default:
        break;
    }
  };

  render() {
    const {
      query,
      results,
      showDropdown,
      isSearching,
      highlightedIndex,
      noResults,
      searchError,
      isUsingCache,
    } = this.state;
    const {
      error,
      disabled,
      fccLoading,
      fccSuccess,
      errorFilerId,
      variant,
    } = this.props;

    const isLoading = isSearching || fccLoading;

    // Material UI variant (addclient.js)
    if (variant === "material") {
      return (
        <div ref={this.wrapperRef} style={{ position: "relative" }}>
          <label
            htmlFor="filer_id"
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "12px",
              fontWeight: "400",
              color: error ? "#f44336" : "rgba(0, 0, 0, 0.54)",
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            }}
          >
            FCC 499 Filer ID *
          </label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              id="filer_id"
              name="filer_id"
              value={query}
              onChange={this.handleInputChange}
              onKeyDown={this.handleKeyDown}
              onBlur={this.handleBlur}
              placeholder="Search by Filer ID or Company Name"
              autoComplete="off"
              disabled={disabled || fccLoading}
              style={{
                width: "100%",
                padding: "16.5px 40px 16.5px 14px",
                border: `1px solid ${error ? "#f44336" : "rgba(0, 0, 0, 0.23)"}`,
                borderRadius: "4px",
                fontSize: "16px",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                outline: "none",
                boxSizing: "border-box",
                backgroundColor: disabled || fccLoading ? "#f5f5f5" : "#fff",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => {
                if (!error) {
                  e.target.style.borderColor = "#1976d2";
                  e.target.style.borderWidth = "2px";
                  e.target.style.padding = "15.5px 39px 15.5px 13px";
                }
              }}
              onBlurCapture={(e) => {
                if (!error) {
                  e.target.style.borderColor = "rgba(0, 0, 0, 0.23)";
                  e.target.style.borderWidth = "1px";
                  e.target.style.padding = "16.5px 40px 16.5px 14px";
                }
              }}
            />
            {isLoading && (
              <span
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <div className="spinner-border spinner-border-sm text-primary"></div>
              </span>
            )}
          </div>
          <p
            style={{
              color: error ? "#f44336" : "rgba(0, 0, 0, 0.6)",
              fontSize: "0.75rem",
              marginTop: "3px",
              marginLeft: "14px",
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            }}
          >
            Search by Filer ID or Company Name (min 3 characters).
          </p>
          {this.renderDropdown(query, results, showDropdown, highlightedIndex, noResults, searchError)}
          {fccSuccess && (
            <div style={{ marginTop: "5px", color: "#1ee0ac" }}>
              <em className="icon ni ni-check-circle"></em> Filer ID verified
              successfully
            </div>
          )}
        </div>
      );
    }

    // Default variant (viewClient.js style)
    return (
      <div ref={this.wrapperRef} style={{ position: "relative" }}>
        <div style={{ position: "relative" }}>
          <input
            className="form-control form-control-lg"
            id="filer_id"
            name="filer_id"
            value={query}
            onChange={this.handleInputChange}
            onKeyDown={this.handleKeyDown}
            onBlur={this.handleBlur}
            placeholder="Search by Filer ID or Company Name"
            autoComplete="off"
            disabled={disabled || fccLoading}
            style={{
              borderColor: errorFilerId === "error" ? "red" : "",
              paddingRight: "40px",
            }}
          />
          {isLoading && (
            <span
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <div className="spinner-border spinner-border-sm text-primary"></div>
            </span>
          )}
        </div>
        {this.renderDropdown(query, results, showDropdown, highlightedIndex, noResults, searchError)}
        {fccSuccess && (
          <div style={{ marginTop: "5px", color: "#1ee0ac" }}>
            <em className="icon ni ni-check-circle"></em> Filer ID verified
            successfully
          </div>
        )}
        {errorFilerId && errorFilerId !== "error" && (
          <small style={{ color: "red" }}>{errorFilerId}</small>
        )}
      </div>
    );
  }

  renderDropdown(query, results, showDropdown, highlightedIndex, noResults, searchError) {
    // Don't show dropdown if not requested or query is too short
    if (!showDropdown || query.trim().length < 3) return null;

    const dropdownStyle = {
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      zIndex: 1000,
      backgroundColor: "#fff",
      border: "1px solid #ddd",
      borderRadius: "4px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      maxHeight: "300px",
      overflowY: "auto",
      marginTop: "2px",
    };

    if (searchError) {
      return (
        <div style={dropdownStyle}>
          <div
            style={{
              padding: "12px 16px",
              color: "#e85347",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            <em className="icon ni ni-alert-circle" style={{ marginRight: "6px" }}></em>
            {searchError}
          </div>
        </div>
      );
    }

    if (noResults) {
      return (
        <div style={dropdownStyle}>
          <div
            style={{
              padding: "12px 16px",
              color: "#8094ae",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            No results found
          </div>
        </div>
      );
    }

    if (results.length === 0) return null;

    return (
      <div style={dropdownStyle}>
        {results.map((item, index) => (
          <div
            key={item.filer_id}
            onMouseDown={(e) => {
              e.preventDefault();
              this.handleSelect(item);
            }}
            onMouseEnter={() => this.setState({ highlightedIndex: index })}
            style={{
              padding: "10px 16px",
              cursor: "pointer",
              backgroundColor:
                highlightedIndex === index ? "#f0f4ff" : "transparent",
              borderBottom:
                index < results.length - 1 ? "1px solid #f0f0f0" : "none",
              fontSize: "14px",
              color: "#364a63",
              transition: "background-color 0.15s",
            }}
          >
            <span style={{ fontWeight: "500" }}>
              {this.formatLabel(item, query)}
            </span>
          </div>
        ))}
      </div>
    );
  }
}

export default FccFilerAutocomplete;
